import { Directive, ElementRef, forwardRef, HostListener, Input, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DecimalToCommaPipe } from '../pipes/decimal-to-comma.pipe';
import { CommaToDecimalPipe } from '../pipes/comma-to-decimal.pipe';

@Directive({
	selector: '[appFormatCurrency]',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => FormatCurrencyDirective),
			multi: true,
		},
	],
})
export class FormatCurrencyDirective implements ControlValueAccessor {
	@Input() format!: string;
	@Input() min!: number;
	@Input() max!: number;
	@Input() maxDecimals = 2;

	onChangeCallback = (_: any) => {};
	onTouchedCallback = (_: any) => {};

	private restrict = '[0-9,]';

	private allowedControlKeyCombinationChars = ['v', 'c', 'V', 'C', 'a', 'A', 'x', 'X'];
	private allowedSystemKeyCodes = [8, 9, 13, 16, 18, 20, 27, 42, 43, 44, 45, 46, 37, 39, 35, 36];

	constructor(private _renderer: Renderer2, private _elementRef: ElementRef) {}

	writeValue(value: string) {
		this._renderer.setProperty(
			this._elementRef.nativeElement,
			'value',
			new DecimalToCommaPipe().transform(value, this.maxDecimals)
		);
	}

	registerOnChange(fn: any) {
		this.onChangeCallback = fn;
	}

	registerOnTouched(fn: any) {
		this.onTouchedCallback = fn;
	}

	@HostListener('blur', ['$event']) onBlur($event: any) {
		this.onTouchedCallback($event);
	}

	@HostListener('keydown', ['$event']) keydown(event: KeyboardEvent) {
		const oldValue = this._elementRef.nativeElement.value;

		let code;
		if (event.keyCode !== undefined) {
			code = event.keyCode;
		} else {
			code = event.which;
		}
		if (this.allowedSystemKeyCodes.indexOf(code) !== -1) {
			return;
		}

		let character;
		if (event.key !== undefined) {
			character = event.key;
		} else {
			character = String.fromCharCode(code);
		}

		if (character === 'Decimal') {
			character = ',';
		}

		if (event.ctrlKey && this.allowedControlKeyCombinationChars.indexOf(character) !== -1) {
			return;
		}

		if (!this.isCharacterValid(character, this._elementRef.nativeElement.selectionStart)) {
			event.preventDefault();
			event.stopImmediatePropagation();
		}
		if (this.format) {
			let integerPlaces = 0;
			let decimalPlaces = 0;

			let separatorPosition = this.format.indexOf(',');
			if (separatorPosition === -1) {
				separatorPosition = this.format.indexOf('.');
			}

			if (separatorPosition !== -1) {
				integerPlaces = separatorPosition;
				decimalPlaces = this.format.substring(separatorPosition + 1).length;
			} else {
				integerPlaces = this.format.length;
			}

			// costruct new value
			const caretPosition = this._elementRef.nativeElement.selectionStart;
			const selectionEnd = this._elementRef.nativeElement.selectionEnd;

			let newValue = '';
			if (caretPosition === oldValue.length) {
				newValue = oldValue + character;
			} else {
				newValue = oldValue.substr(0, caretPosition) + character + oldValue.substr(selectionEnd);
			}
			const parts = newValue.split(',');
			if (parts[0].length > integerPlaces || (parts.length > 1 && parts[1].length > decimalPlaces)) {
				event.preventDefault();
				event.stopImmediatePropagation();
			}
		}
	}

	@HostListener('input', ['$event.target.value']) input(value: any) {
		if (value == null || value === '') {
			this._renderer.setProperty(this._elementRef.nativeElement, 'value', null);
			this.onChangeCallback(null);
			return;
		}
		if (value) {
			const maxDecimals = this.maxDecimals;
			if (maxDecimals == undefined || typeof maxDecimals != 'number') {
				this.maxDecimals = 2;
			}

			const stringValue = value.toString();
			const commaPosition = stringValue.indexOf(',');

			// Handle cases where a comma is found
			if (commaPosition > -1) {
				// Check if there are additional commas after the first one
				const nextCommaPosition = stringValue.indexOf(',', commaPosition + 1);
				if (nextCommaPosition > -1) {
					// Additional comma found: cut off the second comma and everything after it
					value = stringValue.substring(0, nextCommaPosition);
				} else if (maxDecimals === 0) {
					// No decimals allowed: remove the comma and all decimals
					value = stringValue.substring(0, commaPosition);
				} else if (stringValue.substring(commaPosition + 1).length > maxDecimals && maxDecimals >= 0) {
					// More decimals than allowed: truncate to `maxDecimals` decimal places
					value = stringValue.substring(0, commaPosition + 1 + maxDecimals);
				}
				// Update the element's value
				this._renderer.setProperty(this._elementRef.nativeElement, 'value', value);
			}

			// Transform the value using CommaToDecimalPipe
			const transformedValue = new CommaToDecimalPipe().transform(value) || 0;

			// Remove leading zero if necessary
			if (value[0] === '0' && value[1] !== ',' && transformedValue > 0) {
				value = value.substring(1);
				this._renderer.setProperty(this._elementRef.nativeElement, 'value', value);
			}

			// Trigger the onChange callback with the transformed value
			this.onChangeCallback(transformedValue);
		}
	}

	@HostListener('paste', ['$event']) paste(event: any) {
		const oldValue = this._elementRef.nativeElement.value;
		const data = event.clipboardData.getData('text');
		let res = '';
		for (let i = 0; i < data.length; i++) {
			if (data[i] === '-') {
				if (res.length === 0) res += data[i];
			} else if (this.isCharacterValid(data[i])) {
				res += data[i];
			}
		}
		if (this.format) {
			let integerPlaces = 0;
			let decimalPlaces = 0;

			let separatorPosition = this.format.indexOf(',');
			if (separatorPosition === -1) {
				separatorPosition = this.format.indexOf('.');
			}

			if (separatorPosition !== -1) {
				integerPlaces = separatorPosition;
				decimalPlaces = this.format.substring(separatorPosition + 1).length;
			} else {
				integerPlaces = this.format.length;
			}
			const parts = res.split(',');
			if (parts[0].length > integerPlaces || (parts.length > 1 && parts[1].length > decimalPlaces)) {
				event.preventDefault();
				event.stopImmediatePropagation();
				return;
			}
		}
		if (data !== res) {
			event.preventDefault();
			this._renderer.setProperty(this._elementRef.nativeElement, 'value', res);
			const ev = new Event('input', { bubbles: true });
			this._elementRef.nativeElement.dispatchEvent(ev);
		}
	}

	private isCharacterValid(character: any, position?: number) {
		if (character === '-') {
			const currentValue = this._elementRef.nativeElement.value;
			return position === 0 && !currentValue.includes('-');
		}
		const restrictExpression = new RegExp(this.restrict);
		return character && restrictExpression.test(character);
	}
}
